import { useState, useMemo } from 'react';
import {
  Plus,
  Loader2,
  BedDouble,
  Pencil,
  Trash2,
  Layers,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { AccessDenied } from '@/components/ui/AccessDenied';
import { UserRole } from '@pms/shared';
import { useAuth } from '@/hooks/useAuth';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import {
  useRoomTypes,
  useCreateRoomType,
  useUpdateRoomType,
  useDeleteRoomType,
  useRoomTypeCategories,
  useCreateRoomTypeCategory,
  useUpdateRoomTypeCategory,
  useDeleteRoomTypeCategory,
} from '@/hooks/api/useRevenue';
import type {
  RoomType,
  CreateRoomTypePayload,
  UpdateRoomTypePayload,
  RoomTypeCategory,
  CreateRoomTypeCategoryPayload,
  UpdateRoomTypeCategoryPayload,
} from '@/services/api/revenue.api';

type TabKey = 'room-types' | 'categories';

// ─── Page ────────────────────────────────────────────────────────────────────

export function RoomTypesPage() {
  const { user } = useAuth();
  const canAccess = user?.role === UserRole.ADMIN || user?.role === UserRole.REVENUE_MANAGER;
  const [activeTab, setActiveTab] = useState<TabKey>('room-types');
  const propertyId = useSelectedPropertyId();

  if (!canAccess) {
    return <AccessDenied message="Room type management is restricted to Admins and Revenue Managers." />;
  }

  return (
    <div>
      <PageHeader
        title="Room Types"
        description="Manage room types and categories"
      />

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6" role="tablist" aria-label="Room types sections">
        <div className="flex gap-0 -mb-px">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'room-types'}
            onClick={() => setActiveTab('room-types')}
            className={cn(
              'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'room-types'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
            )}
          >
            <BedDouble size={16} aria-hidden="true" />
            Room Types
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'categories'}
            onClick={() => setActiveTab('categories')}
            className={cn(
              'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'categories'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
            )}
          >
            <Layers size={16} aria-hidden="true" />
            Categories
          </button>
        </div>
      </div>

      {activeTab === 'room-types' && <RoomTypesTab propertyId={propertyId} />}
      {activeTab === 'categories' && <CategoriesTab />}
    </div>
  );
}

// ─── Room Types Tab ──────────────────────────────────────────────────────────

function RoomTypesTab({ propertyId }: { propertyId: string }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<RoomType | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const { data: roomTypes, isLoading } = useRoomTypes({ propertyId: propertyId || undefined });
  const { data: categories } = useRoomTypeCategories();
  const deleteMutation = useDeleteRoomType();

  const allItems: RoomType[] = roomTypes?.data ?? [];
  const catList: RoomTypeCategory[] = categories?.data ?? [];

  const items: RoomType[] = useMemo(() => {
    if (!searchTerm) return allItems;
    const q = searchTerm.toLowerCase();
    return allItems.filter(
      (rt) => rt.name.toLowerCase().includes(q) || rt.code.toLowerCase().includes(q),
    );
  }, [allItems, searchTerm]);

  const handleDelete = (id: string) => {
    if (window.confirm('Delete this room type?')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <input
          type="text"
          placeholder="Search by name or code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
          aria-label="Search room types"
        />
        <Button onClick={() => { setEditing(null); setShowForm(true); }}>
          <Plus size={16} aria-hidden="true" />
          Add Room Type
        </Button>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : items.length === 0 ? (
        <EmptyState icon={BedDouble} title="No room types" description="Create your first room type." actionLabel="Add Room Type" onAction={() => setShowForm(true)} />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Code</Th>
                  <Th>Category</Th>
                  <Th>Max Occ.</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((rt) => (
                  <tr key={rt.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{rt.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">{rt.code}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{rt.category?.name ?? '--'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{rt.maxOccupancy}</td>
                    <td className="px-4 py-3">
                      <span className={cn(
                        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                        rt.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500',
                      )}>
                        {rt.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => { setEditing(rt); setShowForm(true); }} className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50" aria-label="Edit">
                          <Pencil size={14} />
                        </button>
                        <button type="button" onClick={() => handleDelete(rt.id)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" aria-label="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SlideOver isOpen={showForm} onClose={() => { setShowForm(false); setEditing(null); }} title={editing ? 'Edit Room Type' : 'Add Room Type'}>
        <RoomTypeForm propertyId={propertyId} editing={editing} categories={catList} onClose={() => { setShowForm(false); setEditing(null); }} />
      </SlideOver>
    </div>
  );
}

function RoomTypeForm({
  propertyId,
  editing,
  categories,
  onClose,
}: {
  propertyId: string;
  editing: RoomType | null;
  categories: RoomTypeCategory[];
  onClose: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [code, setCode] = useState(editing?.code ?? '');
  const [categoryId, setCategoryId] = useState(editing?.categoryId ?? '');
  const [maxOccupancy, setMaxOccupancy] = useState(String(editing?.maxOccupancy ?? 2));
  const [description, setDescription] = useState(editing?.description ?? '');

  const createMutation = useCreateRoomType();
  const updateMutation = useUpdateRoomType();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editing) {
      const payload: UpdateRoomTypePayload = { name, code, categoryId: categoryId || undefined, maxOccupancy: Number(maxOccupancy), description: description || undefined };
      updateMutation.mutate({ id: editing.id, payload }, { onSuccess: () => onClose() });
    } else {
      const payload: CreateRoomTypePayload = { propertyId, name, code, categoryId: categoryId || undefined, maxOccupancy: Number(maxOccupancy), description: description || undefined };
      createMutation.mutate(payload, { onSuccess: () => onClose() });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="rt-name" className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
        <input id="rt-name" type="text" required value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div>
        <label htmlFor="rt-code" className="block text-sm font-medium text-gray-700 mb-1">Code <span className="text-red-500">*</span></label>
        <input id="rt-code" type="text" required maxLength={50} value={code} onChange={(e) => setCode(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div>
        <label htmlFor="rt-cat" className="block text-sm font-medium text-gray-700 mb-1">Category</label>
        <select id="rt-cat" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
          <option value="">None</option>
          {categories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
        </select>
      </div>
      <div>
        <label htmlFor="rt-occ" className="block text-sm font-medium text-gray-700 mb-1">Max Occupancy</label>
        <input id="rt-occ" type="number" min={1} value={maxOccupancy} onChange={(e) => setMaxOccupancy(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div>
        <label htmlFor="rt-desc" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea id="rt-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending}>{editing ? 'Update' : 'Create'}</Button>
      </div>
    </form>
  );
}

// ─── Categories Tab ──────────────────────────────────────────────────────────

function CategoriesTab() {
  const { data: categories, isLoading } = useRoomTypeCategories();
  const createMutation = useCreateRoomTypeCategory();
  const updateMutation = useUpdateRoomTypeCategory();
  const deleteMutation = useDeleteRoomTypeCategory();

  const [showForm, setShowForm] = useState(false);
  const [editingCat, setEditingCat] = useState<RoomTypeCategory | null>(null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');

  const items: RoomTypeCategory[] = categories?.data ?? [];

  const openEdit = (cat: RoomTypeCategory) => {
    setEditingCat(cat);
    setName(cat.name);
    setCode(cat.code);
    setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCat) {
      const payload: UpdateRoomTypeCategoryPayload = { name, code };
      updateMutation.mutate({ id: editingCat.id, payload }, { onSuccess: () => { setShowForm(false); setEditingCat(null); } });
    } else {
      const payload: CreateRoomTypeCategoryPayload = { name, code };
      createMutation.mutate(payload, { onSuccess: () => { setShowForm(false); } });
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Delete this category?')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={() => { setEditingCat(null); setName(''); setCode(''); setShowForm(true); }}>
          <Plus size={16} aria-hidden="true" />
          Add Category
        </Button>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : items.length === 0 ? (
        <EmptyState icon={Layers} title="No categories" description="Create categories to organize room types." />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr><Th>Name</Th><Th>Code</Th><Th>Actions</Th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((cat) => (
                  <tr key={cat.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{cat.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">{cat.code}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => openEdit(cat)} className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50" aria-label="Edit"><Pencil size={14} /></button>
                        <button type="button" onClick={() => handleDelete(cat.id)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" aria-label="Delete"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SlideOver isOpen={showForm} onClose={() => { setShowForm(false); setEditingCat(null); }} title={editingCat ? 'Edit Category' : 'Add Category'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="cat-name" className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
            <input id="cat-name" type="text" required value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <div>
            <label htmlFor="cat-code" className="block text-sm font-medium text-gray-700 mb-1">Code <span className="text-red-500">*</span></label>
            <input id="cat-code" type="text" required value={code} onChange={(e) => setCode(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <Button type="button" variant="secondary" onClick={() => { setShowForm(false); setEditingCat(null); }}>Cancel</Button>
            <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending}>{editingCat ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </SlideOver>
    </div>
  );
}

// ─── Shared ──────────────────────────────────────────────────────────────────

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-20">
      <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}
